import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { checkSSOByEmail, initiateSSO } from "api/ssoService";

import { BaseButton, BaseInput, Heading } from "components/base";
import FieldErrorMessage from "components/base/FieldErrorMessage";
import { IconSpinner } from "components/icons";
import LoginTypeSelector, {
  LOGIN_TYPE_SSO,
  LoginType
} from "components/login/LoginTypeSelector";

import "./SSO.scss";

export default function SSO() {
  const history = useHistory();
  const [loginType, setLoginType] = useState<LoginType>(LOGIN_TYPE_SSO);
  const [email, setEmail] = useState("");
  const [ssoError, setSsoError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Check for email and error passed from Login redirect
    const locationState = history.location.state as
      | { email?: string; ssoError?: string }
      | undefined;
    if (locationState?.email) {
      setEmail(locationState.email);
      if (locationState.ssoError) {
        setSsoError(locationState.ssoError);
      }
      // Clear the state to prevent re-filling on refresh
      history.replace("/sso", {});
    }
  }, [history]);

  const handleSSOLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.trim()) {
      setSsoError("Email address is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSsoError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setSsoError("");

    try {
      // Check if the email domain has SSO enabled
      const organizationId = await checkSSOByEmail(email);

      if (!organizationId) {
        setSsoError("SSO not configured for this domain");
        setLoading(false);

        // Redirect to login page with email pre-filled and error message
        setTimeout(() => {
          history.push("/login", {
            email,
            ssoError: "SSO not configured for this domain"
          });
        }, 800);
        return;
      }

      // Initiate SSO flow with the organization ID
      await initiateSSO(organizationId, "/app");
    } catch (err) {
      setLoading(false);
      setSsoError(
        "Failed to initiate SSO login. Please try again or contact your administrator."
      );
    }
  };

  return (
    <div className="content-container">
      <div>
        <Heading className="align-center large" element="h3">
          Sign in
        </Heading>

        <LoginTypeSelector value={loginType} onChange={setLoginType} />

        <div className="flex items-center justify-center">
          {ssoError && <FieldErrorMessage message={ssoError} />}
        </div>

        <div className="loginTransitionContainer ssoHeight">
          <form name="login" onSubmit={handleSSOLogin} className="sso-login-section">
            <fieldset className="fieldset">
              <BaseInput
                type="email"
                value={email}
                label="Company email address"
                required
                onChange={setEmail}
                placeholder="email@company.com"
                disabled={loading}
                autoComplete="username"
                id="email-sso-input"
              />

              <div
                className="flex items-center justify-center"
                style={{ marginTop: "3rem" }}>
                <BaseButton
                  type="submit"
                  appearance="primary"
                  data-testid="sso-submit-button"
                  disabled={loading || !email.trim()}
                  style={{ width: "320px" }}>
                  {loading ? "Checking..." : "Login"}
                </BaseButton>
              </div>
            </fieldset>
          </form>
        </div>

        {loading && (
          <div className="flex items-center justify-center" style={{ marginTop: "1rem" }}>
            <IconSpinner />
          </div>
        )}
      </div>
    </div>
  );
}
